/**
 * Interactive HTML pages inline in chat.
 *
 * An agent writes one self-contained page to its workspace
 * (`/workspace/pages/<name>.html`) and embeds it in a reply as a markdown
 * image, `![Title](pages/<name>.html)`. The client frames the page's signed
 * workspace asset URL with `?render=inline`; the server answers that variant
 * with the page rewritten by `buildInlineHtmlPage` (vendored chart libraries
 * instead of CDN tags, a small bootstrap at the very start of <head>) under
 * the strict policy from `htmlPageResponseHeaders`. The page runs in an
 * opaque-origin sandbox that may run its own inline script and the three
 * vendored libraries, and may load nothing else: no fetch, no images or fonts
 * from the network, no frames, no forms.
 *
 * The bootstrap speaks the MCP Apps protocol (JSON-RPC over postMessage) with
 * its host, like upstream T3's `html_render`: the page reports its content
 * height, asks the host to open links, and receives the app theme as CSS
 * custom properties (first through the URL fragment, then live).
 *
 * Pure string work only: the server route, the web client and the Playwright
 * fixture all build the same document from this module, so the browser tests
 * exercise the exact policy production sends.
 *
 * Adapted from pingdotgg/t3code (MIT), packages/shared/src/htmlRender.ts at 7202ba666.
 */
import { CHART_CATEGORICAL_COLORS, type ChartPaletteMode } from "./chartPalette.ts";

/** The asset query that asks for the inline page variant (`?render=inline`). */
export const HTML_PAGE_RENDER_PARAM = "render";
export const HTML_PAGE_RENDER_INLINE = "inline";

/** Pages larger than this are refused (413): an inline page is a figure, not a site. */
export const HTML_PAGE_MAX_BYTES = 2 * 1024 * 1024;

export const HTML_PAGE_MIN_HEIGHT = 80;
export const HTML_PAGE_MAX_HEIGHT = 2000;
/** The box an inline page holds before it reports its own height. */
export const HTML_PAGE_PLACEHOLDER_HEIGHT = 360;

/** Where the server serves the vendored chart libraries. Ends with `/`: it is a CSP path. */
export const HTML_PAGE_LIBRARY_ROUTE_PREFIX = "/api/html-page/lib/";

/**
 * The libraries a page may load, by npm name, with the file the server serves
 * each one as. A page asks for them with the CDN `<script src>` it would write
 * anywhere; `rewriteHtmlPageMarkup` points those tags at the server.
 */
export const HTML_PAGE_LIBRARIES = {
  echarts: { file: "echarts.js", global: "echarts" },
  "chart.js": { file: "chart.js", global: "Chart" },
  d3: { file: "d3.js", global: "d3" },
} as const;

export type HtmlPageLibraryName = keyof typeof HTML_PAGE_LIBRARIES;

const LIBRARY_NAMES = Object.keys(HTML_PAGE_LIBRARIES) as HtmlPageLibraryName[];

/** The library served under a route file name (`echarts.js`), if any. */
export function htmlPageLibraryForFile(file: string): HtmlPageLibraryName | null {
  return LIBRARY_NAMES.find((name) => HTML_PAGE_LIBRARIES[name].file === file) ?? null;
}

/** Whether a workspace path names an HTML page (`.html` / `.htm`). */
export function isHtmlPagePath(path: string): boolean {
  return /\.html?$/i.test(path.split(/[?#]/, 1)[0] ?? "");
}

// ---------------------------------------------------------------------------
// CDN script tags → vendored libraries
// ---------------------------------------------------------------------------

// The full builds each library publishes, lowercased. Any version maps to the
// one version the server vendors; subset builds (echarts.common) map to the
// full build, which is a superset. Extensions, plugins and ESM entry points
// (`echarts/dist/extension/*`, `+esm`) are not here and stay blocked.
const LIBRARY_BUILDS: Readonly<Record<HtmlPageLibraryName, ReadonlySet<string>>> = {
  echarts: new Set([
    "echarts.js",
    "echarts.min.js",
    "echarts.common.js",
    "echarts.common.min.js",
    "echarts.simple.js",
    "echarts.simple.min.js",
  ]),
  "chart.js": new Set([
    "chart.umd.js",
    "chart.umd.min.js",
    "chart.min.js",
    "chart.js",
    "chart.bundle.js",
    "chart.bundle.min.js",
  ]),
  d3: new Set(["d3.js", "d3.min.js"]),
};

// jsDelivr and unpkg address npm packages: `/npm/<name>@<version>/<file>`.
// The package name must end at `@`, `/` or the end, so `echarts-gl`,
// `d3-sankey` and `chartjs-plugin-*` never match.
const NPM_CDN =
  /^(?:https?:)?\/\/(?:(?:cdn|fastly)\.jsdelivr\.net\/npm|unpkg\.com)\/(echarts|chart\.js|d3)(?:@[^/?#]*)?(?:\/([^?#]*))?(?:[?#].*)?$/;
// cdnjs names Chart.js with capitals and drops the dist/ directory.
const CDNJS =
  /^(?:https?:)?\/\/cdnjs\.cloudflare\.com\/ajax\/libs\/(echarts|chart\.js|d3)\/[^/]+\/([^?#]+)(?:[?#].*)?$/;

/**
 * The vendored library a CDN script URL loads, or `null` for any other URL.
 * Only jsDelivr, unpkg and cdnjs URLs for the full UMD builds match.
 */
export function htmlPageLibraryForUrl(rawUrl: string): HtmlPageLibraryName | null {
  const url = rawUrl.trim().replaceAll("&amp;", "&").toLowerCase();
  const npm = NPM_CDN.exec(url);
  if (npm) {
    const name = npm[1] as HtmlPageLibraryName;
    const file = (npm[2] ?? "").replace(/\/+$/, "");
    // The bare package URL serves its browser entry, the UMD build.
    if (file === "") return name;
    return file.startsWith("dist/") && LIBRARY_BUILDS[name].has(file.slice("dist/".length))
      ? name
      : null;
  }
  const cdnjs = CDNJS.exec(url);
  if (cdnjs) {
    const name = cdnjs[1] as HtmlPageLibraryName;
    return LIBRARY_BUILDS[name].has(cdnjs[2] ?? "") ? name : null;
  }
  return null;
}

const ATTRIBUTE_VALUE = String.raw`(?:"[^"]*"|'[^']*'|[^\s"'=<>\x60]+)`;
const SRC_ATTRIBUTE = new RegExp(String.raw`(?:^|[\s/])src\s*=\s*(${ATTRIBUTE_VALUE})`, "i");
// Subresource integrity names the CDN's bytes, and `crossorigin` would make
// the request CORS from an opaque origin: both would block the vendored copy.
const REPLACED_SCRIPT_ATTRIBUTES = new RegExp(
  String.raw`(^|[\s/])(?:src|integrity|crossorigin)(?:\s*=\s*${ATTRIBUTE_VALUE})?(?=[\s/]|$)`,
  "gi",
);
const HTTP_EQUIV_REFRESH = new RegExp(
  String.raw`(^|[\s/])http-equiv(\s*=\s*(?:"\s*refresh\s*"|'\s*refresh\s*'|refresh(?=[\s/]|$)))`,
  "i",
);
const TAG_ATTRIBUTES = String.raw`((?:[^>"']|"[^"]*"|'[^']*')*)`;

// Comments and raw-text elements are consumed whole, so a tag written inside
// them (a code sample, a string in a script) is never rewritten.
const MARKUP_TOKEN = new RegExp(
  [
    String.raw`<!--[\s\S]*?(?:-->|$)`,
    String.raw`<(style|textarea|title|xmp|iframe|noembed|noframes|noscript)\b[\s\S]*?(?:<\/\1\s*>|$)`,
    String.raw`<plaintext\b[\s\S]*$`,
    String.raw`<script\b${TAG_ATTRIBUTES}>[\s\S]*?(?:<\/script\b[^>]*>|$)`,
    String.raw`<meta\b${TAG_ATTRIBUTES}>`,
  ].join("|"),
  "gi",
);

const unquote = (value: string) =>
  value.startsWith('"') || value.startsWith("'") ? value.slice(1, -1) : value;

/**
 * Points every CDN `<script src>` for a vendored library at `libraryUrl` and
 * disarms `<meta http-equiv="refresh">`, which would navigate the frame.
 * Every other external script is left as written; the page's policy blocks it.
 */
export function rewriteHtmlPageMarkup(
  html: string,
  libraryUrl: (library: HtmlPageLibraryName) => string,
): string {
  return html.replace(
    MARKUP_TOKEN,
    (match, _raw: string | undefined, scriptAttributes?: string, metaAttributes?: string) => {
      if (scriptAttributes !== undefined) {
        const src = SRC_ATTRIBUTE.exec(scriptAttributes);
        const library = src ? htmlPageLibraryForUrl(unquote(src[1]!)) : null;
        if (library === null) return match;
        const kept = scriptAttributes.replace(REPLACED_SCRIPT_ATTRIBUTES, "$1").trimEnd();
        const start = `<script${kept} src="${libraryUrl(library)}">`;
        return start + match.slice("<script".length + scriptAttributes.length + 1);
      }
      if (metaAttributes !== undefined && HTTP_EQUIV_REFRESH.test(metaAttributes)) {
        return `<meta${metaAttributes.replace(HTTP_EQUIV_REFRESH, "$1data-blocked-http-equiv$2")}>`;
      }
      return match;
    },
  );
}

// ---------------------------------------------------------------------------
// Theme
// ---------------------------------------------------------------------------

/** The CSS custom properties a page is themed with, in the order agents read them. */
export const HTML_PAGE_THEME_VARIABLE_NAMES = [
  "--background",
  "--foreground",
  "--muted",
  "--muted-foreground",
  "--card",
  "--card-foreground",
  "--popover",
  "--popover-foreground",
  "--secondary",
  "--secondary-foreground",
  "--border",
  "--input",
  "--ring",
  "--primary",
  "--primary-foreground",
  "--accent",
  "--accent-foreground",
  "--destructive",
  "--warning",
  "--success",
  "--info",
  "--chart-1",
  "--chart-2",
  "--chart-3",
  "--chart-4",
  "--chart-5",
  "--chart-6",
  "--chart-7",
  "--chart-8",
  "--radius",
  "--font-sans",
  "--font-mono",
] as const;

export type HtmlPageThemeVariable = (typeof HTML_PAGE_THEME_VARIABLE_NAMES)[number];

/** The resolved theme a client hands a page. */
export interface HtmlPageTheme {
  readonly appearance: ChartPaletteMode;
  readonly variables: Readonly<Record<string, string>>;
}

/** The categorical series colours as `--chart-1…--chart-8`. */
export function htmlPageChartVariables(mode: ChartPaletteMode): Record<string, string> {
  return Object.fromEntries(
    CHART_CATEGORICAL_COLORS[mode].map((color, index) => [`--chart-${index + 1}`, color]),
  );
}

const DEFAULT_FONTS = {
  "--font-sans": '-apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif',
  "--font-mono": '"SF Mono", "SFMono-Regular", Menlo, Consolas, "Liberation Mono", monospace',
};

// Only for a page opened without a client theme (the URL by itself): close to
// the stock interface so it still reads, then replaced by the client's own.
const FALLBACK_THEMES: Readonly<Record<ChartPaletteMode, HtmlPageTheme>> = {
  light: {
    appearance: "light",
    variables: {
      "--background": "#ffffff",
      "--foreground": "#0b0b0b",
      "--muted": "#f4f4f2",
      "--muted-foreground": "#6f6d66",
      "--card": "#ffffff",
      "--card-foreground": "#0b0b0b",
      "--popover": "#fcfcfb",
      "--popover-foreground": "#0b0b0b",
      "--secondary": "#f4f4f2",
      "--secondary-foreground": "#0b0b0b",
      "--border": "#e1e0d9",
      "--input": "#e1e0d9",
      "--ring": "#a3a29b",
      "--primary": "#1c1c1a",
      "--primary-foreground": "#ffffff",
      "--accent": "#f4f4f2",
      "--accent-foreground": "#0b0b0b",
      "--destructive": "#e34948",
      "--warning": "#c98500",
      "--success": "#1baf7a",
      "--info": "#2a78d6",
      ...htmlPageChartVariables("light"),
      "--radius": "0.625rem",
      ...DEFAULT_FONTS,
    },
  },
  dark: {
    appearance: "dark",
    variables: {
      "--background": "#121211",
      "--foreground": "#f5f5f4",
      "--muted": "#22221f",
      "--muted-foreground": "#9a988f",
      "--card": "#1a1a19",
      "--card-foreground": "#f5f5f4",
      "--popover": "#1a1a19",
      "--popover-foreground": "#f5f5f4",
      "--secondary": "#22221f",
      "--secondary-foreground": "#f5f5f4",
      "--border": "#2c2c2a",
      "--input": "#2c2c2a",
      "--ring": "#5c5b56",
      "--primary": "#f5f5f4",
      "--primary-foreground": "#121211",
      "--accent": "#22221f",
      "--accent-foreground": "#f5f5f4",
      "--destructive": "#e66767",
      "--warning": "#c98500",
      "--success": "#199e70",
      "--info": "#3987e5",
      ...htmlPageChartVariables("dark"),
      "--radius": "0.625rem",
      ...DEFAULT_FONTS,
    },
  },
};

/** The stock theme for a mode, for a page with no client theme. */
export function htmlPageFallbackTheme(mode: ChartPaletteMode): HtmlPageTheme {
  return FALLBACK_THEMES[mode];
}

/** Agent-facing reference for the injected variables. */
export const HTML_PAGE_THEME_GUIDE = [
  "The interface injects its theme as CSS custom properties on :root, and they follow the reader's theme and light/dark mode live:",
  "--background (the page background, identical to the reply around it), --foreground, --muted, --muted-foreground,",
  "--card, --card-foreground, --popover, --popover-foreground, --secondary, --secondary-foreground, --border, --input, --ring,",
  "--primary, --primary-foreground (solid buttons), --accent, --accent-foreground (hover surfaces),",
  "--destructive, --warning, --success, --info, --chart-1 … --chart-8 (categorical series, in order), --radius, --font-sans, --font-mono.",
  "The base stylesheet sets the html background, colour and font from these and body margin to 0; your own CSS overrides it.",
].join(" ");

/** Agent-facing layout rules for a page that sits inside a reply. */
export const HTML_PAGE_LAYOUT_GUIDE = [
  "The page is borderless on the reply's background and as wide as the reply column.",
  "Use a fluid width with no horizontal padding on the outermost element, and no outer card, border or banner title: the page is part of your reply.",
  "Give charts fixed pixel heights rather than heights that scale with width.",
  "Let content set the page's height. Never use 100vh or height:100% on html or body; the frame grows to fit the page, so they make it grow again and again.",
].join(" ");

// ---------------------------------------------------------------------------
// Host ↔ page messages (MCP Apps JSON-RPC over postMessage)
// ---------------------------------------------------------------------------

// https://github.com/modelcontextprotocol/ext-apps
const HOST_CONTEXT_CHANGED_METHOD = "ui/notifications/host-context-changed";
const OPEN_LINK_METHOD = "ui/open-link";
const SIZE_CHANGED_METHOD = "ui/notifications/size-changed";

export function clampHtmlPageHeight(height: number): number {
  return Math.min(HTML_PAGE_MAX_HEIGHT, Math.max(HTML_PAGE_MIN_HEIGHT, Math.round(height)));
}

/** The content height in a page's `ui/notifications/size-changed` notification. */
export function readHtmlPageContentHeight(data: unknown): number | undefined {
  if (typeof data !== "object" || data === null) return undefined;
  const { jsonrpc, method, params } = data as Record<string, unknown>;
  if (jsonrpc !== "2.0" || method !== SIZE_CHANGED_METHOD) return undefined;
  const height =
    typeof params === "object" && params !== null
      ? (params as { height?: unknown }).height
      : undefined;
  return typeof height === "number" && Number.isFinite(height) && height > 0 ? height : undefined;
}

/** A page's `ui/open-link` request, if `data` is one with an http(s) URL. */
export function readHtmlPageLinkRequest(
  data: unknown,
): { readonly id: string | number; readonly url: string } | undefined {
  if (typeof data !== "object" || data === null) return undefined;
  const { jsonrpc, id, method, params } = data as Record<string, unknown>;
  if (jsonrpc !== "2.0" || method !== OPEN_LINK_METHOD) return undefined;
  if (typeof id !== "string" && typeof id !== "number") return undefined;
  const raw =
    typeof params === "object" && params !== null ? (params as { url?: unknown }).url : undefined;
  if (typeof raw !== "string" || raw.length > 4096) return undefined;
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return undefined;
  }
  return url.protocol === "https:" || url.protocol === "http:" ? { id, url: url.href } : undefined;
}

/** The empty result a host sends back for a page's request. */
export function htmlPageResult(id: string | number) {
  return { jsonrpc: "2.0", id, result: {} } as const;
}

const THEME_FRAGMENT_KEY = "amoreg-theme";

/** URL fragment that hands a page its theme before first paint. */
export function htmlPageThemeFragment(theme: HtmlPageTheme): string {
  return `#${THEME_FRAGMENT_KEY}=${encodeURIComponent(JSON.stringify(theme))}`;
}

/** The `host-context-changed` notification a host posts into a page when the theme changes. */
export function htmlPageThemeMessage(theme: HtmlPageTheme) {
  return {
    jsonrpc: "2.0",
    method: HOST_CONTEXT_CHANGED_METHOD,
    params: { theme: theme.appearance, styles: { variables: theme.variables } },
  } as const;
}

/**
 * The inline-variant URL for a signed workspace asset URL: the same claim with
 * `render=inline`, and the theme in the fragment.
 */
export function htmlPageInlineUrl(assetUrl: string, theme?: HtmlPageTheme): string {
  const [withoutFragment = ""] = assetUrl.split("#", 1);
  const queryAt = withoutFragment.indexOf("?");
  const path = queryAt === -1 ? withoutFragment : withoutFragment.slice(0, queryAt);
  const params = new URLSearchParams(queryAt === -1 ? "" : withoutFragment.slice(queryAt + 1));
  params.set(HTML_PAGE_RENDER_PARAM, HTML_PAGE_RENDER_INLINE);
  return `${path}?${params.toString()}${theme ? htmlPageThemeFragment(theme) : ""}`;
}

// ---------------------------------------------------------------------------
// Bootstrap
// ---------------------------------------------------------------------------

// The frame scrolls a page taller than itself, but a scrollbar inside the
// reply reads as a box within the thread, so it stays hidden.
const BASE_CSS =
  "html{background:var(--background);color:var(--foreground);font-family:var(--font-sans);font-size:14px;line-height:1.5;-webkit-font-smoothing:antialiased;-webkit-text-size-adjust:100%;scrollbar-width:none}" +
  "html::-webkit-scrollbar{display:none}body{margin:0}code,kbd,pre,samp{font-family:var(--font-mono)}";

const THEME_STYLE_ID = "amoreg-page-theme";

/** WebRTC entry points the bootstrap removes (see the residual-risk note below). */
export const HTML_PAGE_REMOVED_GLOBALS = [
  "RTCPeerConnection",
  "webkitRTCPeerConnection",
  "RTCDataChannel",
  "RTCSessionDescription",
  "RTCIceCandidate",
] as const;

function rootRule(theme: HtmlPageTheme): string {
  const declarations = Object.entries(theme.variables)
    .map(([name, value]) => `${name}:${value};`)
    .join("");
  return `:root{color-scheme:${theme.appearance};${declarations}}`;
}

// Runs synchronously at the start of <head>, before any of the page's own
// markup, so it holds for the page's whole life:
//
// - A covert channel the policy cannot name is closed best-effort: WebRTC's
//   constructors are replaced by non-configurable `undefined` (a page can
//   still recover them from a fresh about:blank child frame's realm).
//   Self-navigation cannot be stopped from inside: the Navigation API fires
//   no events in an opaque-origin document. The embedding app's `frame-src`
//   bounds it, and the host tears down a frame that loads twice.
// - The theme arrives in the URL fragment and later as host-context-changed
//   messages from the parent only. It rewrites its own <style> rather than
//   inline properties, so a page's later `:root` rules still win, then drops
//   the fragment so a page's own hash routing never sees it.
// - A link the reader clicks never replaces the page: the page asks its host
//   to open it, and the host opens only http(s) URLs in a new tab.
// - The page reports its content height so the host can fit the frame to it;
//   a frame shorter than its page would scroll inside the thread.
const BOOTSTRAP_SCRIPT = [
  "(function(){",
  'var w=window,d=document,p=w.parent,f=p!==w,n=0,s=d.getElementById("' + THEME_STYLE_ID + '");',
  `${JSON.stringify(HTML_PAGE_REMOVED_GLOBALS)}.forEach(function(k){try{Object.defineProperty(w,k,{value:undefined,writable:false,configurable:false});}catch(e){}});`,
  `var b=${JSON.stringify(BASE_CSS)};`,
  'function a(t){if(!s||!t||typeof t!=="object"||!t.variables||typeof t.variables!=="object")return;var c=":root{color-scheme:"+(t.appearance==="light"?"light":"dark")+";";for(var k in t.variables){if(/^--[a-z0-9-]+$/.test(k))c+=k+":"+String(t.variables[k]).replace(/[;{}<>\\\\]/g,"")+";";}s.textContent=c+"}"+b;}',
  `try{var m=/[#&]${THEME_FRAGMENT_KEY}=([^&]*)/.exec(location.hash);if(m){a(JSON.parse(decodeURIComponent(m[1])));history.replaceState(history.state,"",location.pathname+location.search);}}catch(e){}`,
  `w.addEventListener("message",function(e){if(e.source!==p)return;var x=e.data,q=x&&x.params;if(x&&x.jsonrpc==="2.0"&&x.method===${JSON.stringify(HOST_CONTEXT_CHANGED_METHOD)}&&q&&q.styles)a({appearance:q.theme,variables:q.styles.variables});});`,
  `d.addEventListener("click",function(e){var l=e.isTrusted?e.composedPath().find(function(t){return t&&t.matches&&t.matches("a[href]");}):null,u;if(!l)return;try{u=new URL(l.getAttribute("href"),d.baseURI);}catch(x){return;}if(u.href.split("#")[0]===location.href.split("#")[0])return;e.preventDefault();if(f&&/^https?:$/.test(u.protocol))p.postMessage({jsonrpc:"2.0",id:"amoreg-link-"+(++n),method:${JSON.stringify(OPEN_LINK_METHOD)},params:{url:u.href}},"*");},true);`,
  `if(f){var h,o,z=function(){var r=d.documentElement,v=Math.ceil(r.scrollHeight>r.clientHeight?r.scrollHeight:r.getBoundingClientRect().height);if(v===h)return;h=v;p.postMessage({jsonrpc:"2.0",method:${JSON.stringify(SIZE_CHANGED_METHOD)},params:{height:v}},"*");};if(w.ResizeObserver){o=new ResizeObserver(z);o.observe(d.documentElement);}d.addEventListener("DOMContentLoaded",function(){if(o&&d.body)o.observe(d.body);z();});w.addEventListener("load",z);}`,
  "})();",
].join("");

function bootstrapMarkup(markup: string): string {
  // Without a client theme (the URL opened by itself) the page follows the OS.
  const defaultCss = `${rootRule(FALLBACK_THEMES.dark)}@media (prefers-color-scheme: light){${rootRule(FALLBACK_THEMES.light)}}${BASE_CSS}`;
  return [
    // First: no speculative DNS lookups for the page's links (best-effort;
    // a DNS name is a channel the policy cannot close).
    '<meta http-equiv="x-dns-prefetch-control" content="off">',
    /<meta\s[^>]*charset/i.test(markup.slice(0, 4096)) ? "" : '<meta charset="utf-8">',
    /<meta\s[^>]*name\s*=\s*["']?viewport/i.test(markup)
      ? ""
      : '<meta name="viewport" content="width=device-width, initial-scale=1">',
    `<style id="${THEME_STYLE_ID}">${defaultCss}</style>`,
    `<script>${BOOTSTRAP_SCRIPT}</script>`,
  ].join("");
}

// Quoted attribute values are blanked to the same length, so a `<head>` or
// `<html>` written inside one (`<div title="<head>">`) is not mistaken for
// the tag the bootstrap goes into.
function blankQuotedAttributeValues(scan: string): string {
  let result = "";
  let inTag = false;
  let quote: string | null = null;
  for (let index = 0; index < scan.length; index++) {
    const char = scan[index]!;
    if (quote !== null) {
      if (char === quote) {
        quote = null;
        result += char;
      } else {
        result += " ";
      }
      continue;
    }
    if (inTag) {
      if (char === '"' || char === "'") quote = char;
      else if (char === ">") inTag = false;
      result += char;
      continue;
    }
    if (char === "<" && /[a-z!/]/i.test(scan[index + 1] ?? "")) inTag = true;
    result += char;
  }
  return result;
}

// Comments, raw text, and template contents are blanked to the same length,
// so offsets still line up and inert tags cannot receive the bootstrap.
function blankNonMarkup(html: string): string {
  const scan = html.replace(
    /<!--[\s\S]*?(?:-->|$)|<(script|style|textarea|title|xmp|iframe|noembed|noframes|noscript)\b[\s\S]*?(?:<\/\1\s*>|$)|<plaintext\b[\s\S]*$/gi,
    (match) => " ".repeat(match.length),
  );
  const parts: string[] = [];
  let depth = 0;
  let start = 0;
  let at = 0;
  for (const match of scan.matchAll(/<(\/?)template(?:\s[^>]*)?\/?>/gi)) {
    if (!match[1]) {
      if (depth++ === 0) start = match.index;
    } else if (depth > 0 && --depth === 0) {
      const end = match.index + match[0].length;
      parts.push(scan.slice(at, start), " ".repeat(end - start));
      at = end;
    }
  }
  if (depth > 0) {
    parts.push(scan.slice(at, start), " ".repeat(scan.length - start));
    at = scan.length;
  }
  parts.push(scan.slice(at));
  return parts.join("");
}

/**
 * Inserts the bootstrap at the very start of the document head, so it runs
 * before any of the page's own markup.
 */
export function injectHtmlPageBootstrap(html: string): string {
  const scan = blankNonMarkup(html);
  const markup = bootstrapMarkup(scan);
  // Tags are found with attribute values blanked as well; the charset and
  // viewport checks above still need those values.
  const tags = blankQuotedAttributeValues(scan);
  const headOpen = /<head(?:\s[^>]*)?>/i.exec(tags);
  if (headOpen) {
    const at = headOpen.index + headOpen[0].length;
    return html.slice(0, at) + markup + html.slice(at);
  }
  const htmlOpen = /<html(?:\s[^>]*)?>/i.exec(tags);
  if (htmlOpen) {
    const at = htmlOpen.index + htmlOpen[0].length;
    return `${html.slice(0, at)}<head>${markup}</head>${html.slice(at)}`;
  }
  const doctype = /^\s*<!doctype[^>]*>/i.exec(html);
  if (doctype) {
    const at = doctype[0].length;
    return `${html.slice(0, at)}<head>${markup}</head>${html.slice(at)}`;
  }
  return `<!doctype html><head>${markup}</head>${html}`;
}

/** The page as served inline: vendored libraries, disarmed refreshes, bootstrap first. */
export function buildInlineHtmlPage(
  html: string,
  libraryUrl: (library: HtmlPageLibraryName) => string,
): string {
  return injectHtmlPageBootstrap(rewriteHtmlPageMarkup(html, libraryUrl));
}

// ---------------------------------------------------------------------------
// Response policy
// ---------------------------------------------------------------------------

/** An `http(s)://host[:port]` origin with nothing a CSP could misread. */
export function isHtmlPageServerOrigin(origin: string): boolean {
  return /^https?:\/\/(?:[a-z0-9.-]+|\[[0-9a-f:.]+\])(?::\d{1,5})?$/i.test(origin);
}

/**
 * The policy an inline page runs under. Everything is denied by default;
 * the page may run its own inline script (it is an interactive page) and the
 * vendored libraries from the server's library path, style itself inline,
 * and use data:/blob: images, fonts and media it builds itself. It cannot
 * fetch, frame, post a form, set a base URL or load a worker, and the
 * `sandbox` gives it an opaque origin, so the app's cookies, storage and
 * same-origin APIs are out of reach even on the server's own origin.
 *
 * `'unsafe-eval'` is allowed: d3's CSV parsing compiles row converters with
 * `new Function`, and eval reaches nothing inline script cannot already reach
 * — every network directive above still applies to evaluated code.
 * `webrtc 'block'` is the CSP3 directive for peer connections; browsers that
 * do not know it ignore it, so the bootstrap also removes the constructors.
 *
 * `serverOrigin` is absolute on purpose: what `'self'` means for a sandboxed
 * opaque-origin document differs between engines.
 */
export function htmlPageContentSecurityPolicy(serverOrigin: string): string {
  if (!isHtmlPageServerOrigin(serverOrigin)) {
    throw new Error(`Not a server origin: ${serverOrigin}`);
  }
  return [
    "default-src 'none'",
    `script-src 'unsafe-inline' 'unsafe-eval' ${serverOrigin}${HTML_PAGE_LIBRARY_ROUTE_PREFIX}`,
    "style-src 'unsafe-inline'",
    "img-src data: blob:",
    "font-src data:",
    "media-src data: blob:",
    "connect-src 'none'",
    "frame-src 'none'",
    "child-src 'none'",
    "worker-src 'none'",
    "manifest-src 'none'",
    "form-action 'none'",
    "base-uri 'none'",
    "webrtc 'block'",
    "sandbox allow-scripts",
  ].join("; ");
}

/** Every header an inline page goes out with. */
export function htmlPageResponseHeaders(serverOrigin: string): Record<string, string> {
  return {
    "Content-Type": "text/html; charset=utf-8",
    "X-Content-Type-Options": "nosniff",
    // The agent can rewrite the file on any turn.
    "Cache-Control": "private, no-store",
    "Content-Security-Policy": htmlPageContentSecurityPolicy(serverOrigin),
    // The page URL carries the signed asset claim; no request may repeat it.
    "Referrer-Policy": "no-referrer",
  };
}

/** Headers for a vendored library (`?v=<version>` in the URL makes them immutable). */
export const HTML_PAGE_LIBRARY_RESPONSE_HEADERS: Readonly<Record<string, string>> = {
  "Content-Type": "text/javascript; charset=utf-8",
  "X-Content-Type-Options": "nosniff",
  "Cache-Control": "public, max-age=31536000, immutable",
  // The page that loads it has an opaque origin.
  "Cross-Origin-Resource-Policy": "cross-origin",
};
